
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import moment from "moment";
import { DateTime, Settings } from "luxon";
import { Datetime } from "vue-datetime";
import { validationMixin } from "vuelidate";

import { UserPermissions } from "@/store/types";
import { AreaNote } from "@/store/area-notes/types";

const AreaNotes = namespace("areaNotes");

Settings.defaultLocale = "en";

@Component({
	mixins: [validationMixin],
	components: {
		"date-time": Datetime,
	},
	validations: {
		newAreaNote: {
			entry: {
				required: function(value) {
					return this.isAreaNoteValid;
				},
			},
			endAt: {
				required: function(value) {
					if (this.isEndTimeOptional) {
						return true;
					}
					return !!value;
				},
				minTime: function(value) {
					if (this.isEndTimeOptional || !value) {
						return true;
					}
					return moment(this.newAreaNote.endAt).isAfter(this.newAreaNote.startFrom);
				}
			}
		},
		newAreaNoteEndTime: {
			minTime: function(value) {
				if (this.isEndTimeOptional) {
					return true;
				}
				return moment(this.newAreaNote.endAt).isAfter(this.newAreaNote.startFrom);
			}
		}
	}
})
export default class AreaNotesForm extends Vue {
	@Getter("getUserName")
	public currentUserName!: string;
	@Getter getPermissions: UserPermissions;

	@AreaNotes.Action public addAreaNote: any;
	@AreaNotes.Action public cancelEditingNote: any;
	@AreaNotes.Action public editAreaNote: any;
	@AreaNotes.Getter public getCurrentGroupId: number;
	@AreaNotes.Getter public getCurrentGroupTitle: string;
	@AreaNotes.Getter public getIsFormShown: boolean;
	@AreaNotes.Getter public getAreaNoteToEdit: AreaNote;
	@AreaNotes.Getter public getIsEditing: boolean;
	@AreaNotes.Mutation public setIsFormShown: any;

	@Prop({ type: Number, default: 0 }) public groupId: number;

	public mounted(): void {
		this.defaultAreaNote.groupId = this.formGroupId;

		if (this.getIsEditing) {
			this.newAreaNote = { ...this.getAreaNoteToEdit };
		}
		this.newAreaNote = { ...this.defaultAreaNote };
	}

	@Watch("getAreaNoteToEdit")
	private getAreaNoteToEditUpdated(newVal, oldVal): void {
		if (newVal !== null) {
			this.newAreaNote = { ...newVal };
		}
	}

	private defaultAreaNote: AreaNote = {
		groupId: this.getCurrentGroupId,
		entry: "",
		requiresAcknowledgement: false,
		noteType: "normal",
		startFrom: null,
		endAt: null,
		created: "",
		userName: this.currentUserName
	};
	private newAreaNote: AreaNote = { ...this.defaultAreaNote };

	private get formGroupId(): number {
		return this.groupId != 0 ? this.groupId : this.getCurrentGroupId;
	}

	private maxNoteLength: number = 500;
	private isSaving = false;
	private UTC: string = "UTC";

	private async onSave(): Promise<void> {
		this.isSaving = true;
		if (this.isNewAreaNoteValid()) {
			if (this.getIsEditing) {
				this.editAreaNote(this.newAreaNote)
					.then(wasSuccess => {
						if (wasSuccess) {
							this.resetForm();
						} else {
							this.$notify({
								type: "error",
								title: "Failed to edit note",
								text: "Please drop the guard inside an existing patrol zone"
							});
						}
					})
					.finally(() => {
						this.isSaving = false;
					});
			} else {
				this.addAreaNote(this.newAreaNote)
					.then(newNote => {
						// If a note has been returned, adding the note was a success
						if (newNote) {
							this.resetForm();
						}
					})
					.finally(() => {
						this.isSaving = false;
					});
			}
		}
	}

	private onCancel(): void {
		if (this.getIsEditing) {
			this.cancelEditingNote();
		} else {
			this.setIsFormShown(false);
		}
		this.resetForm();
	}

	private isNewAreaNoteValid(): boolean {
		this.$v.$touch();
		return !this.$v.$error;
	}

	private resetForm(): void {
		this.setIsFormShown(false);
		this.newAreaNote = { ...this.defaultAreaNote };
		this.newAreaNote.groupId = this.formGroupId;
		this.$v.$reset();
	}

	private clearEndDate(): void {
		this.newAreaNote.endAt = null;
		this.isNewAreaNoteValid();
	}

	private clearStartDate(): void {
		this.newAreaNote.startFrom = null;
		this.isNewAreaNoteValid();
	}

	// Disables the selection of an end date that is before the start date
	private get minimumEndDate(): string {
		if (!this.newAreaNote.startFrom) {
			return null;
		}

		return DateTime
			.fromISO(this.newAreaNote.startFrom, { zone: this.UTC })
			.plus({ minutes: 1 })
			.toISO();
	}

	private get isEndTimeOptional(): boolean {
		return this.newAreaNote.startFrom === null || this.newAreaNote.startFrom === "";
	}

	@Watch("newAreaNote", { deep: true })
	private onNewAreaNoteChange(newVal, oldVal): void {
		this.isNewAreaNoteValid();
	}

	private hasPermissionsToEditNotes(): boolean {
		return this.getPermissions.canViewSiteSetup;
	}

	private get isAreaNoteValid(): boolean {
		return !!this.newAreaNote && !!this.newAreaNote.entry && !!this.newAreaNote.entry.length && this.newAreaNote.entry.length > 0 &&  this.newAreaNote.entry.length <= this.maxNoteLength;
	}
}
